import { useState, useEffect } from "react";

/**
 * useDebounce - Hook para debouncing de valores
 * 
 * Retrasa la actualización de un valor hasta que haya pasado
 * un tiempo determinado sin cambios. Útil para búsquedas,
 * validaciones en tiempo real, etc.
 * 
 * @param {any} value - Valor a debounce
 * @param {number} delay - Delay en milisegundos (default: 500ms)
 * @returns {any} Valor debounced
 * 
 * @example
 * const [searchQuery, setSearchQuery] = useState('');
 * const debouncedQuery = useDebounce(searchQuery, 500);
 * 
 * useEffect(() => {
 *   if (debouncedQuery) {
 *     // Hacer búsqueda
 *     fetchResults(debouncedQuery);
 *   }
 * }, [debouncedQuery]);
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Establecer timeout para actualizar el valor
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpiar timeout si el valor cambia antes del delay
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * useDebouncedCallback - Hook para debouncing de callbacks
 * 
 * Similar a useDebounce pero para funciones. Útil cuando
 * quieres ejecutar una función después de un delay.
 * 
 * @param {Function} callback - Función a ejecutar
 * @param {number} delay - Delay en milisegundos (default: 500ms)
 * @returns {Function} Función debounced
 * 
 * @example
 * const handleSearch = useDebouncedCallback((query) => {
 *   fetchResults(query);
 * }, 500);
 * 
 * <input onChange={(e) => handleSearch(e.target.value)} />
 */
export const useDebouncedCallback = (callback, delay = 500) => {
  const [timeoutId, setTimeoutId] = useState(null);

  const debouncedCallback = (...args) => {
    // Limpiar timeout anterior
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Establecer nuevo timeout
    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  };

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
};

export default useDebounce;
